"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, CalendarCheck, Check, Gift, Image as ImageIcon, QrCode } from "lucide-react";
import BuyEventButton from "../buyEventButton";
import { Link } from "@/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useCurrencyPrices } from "@/hooks/useCurrencyPrices";
import { DetectedLocalPricingNudge } from "@/components/index/LocalPricingNudge";
import { formatOfferPrice, type PlanPackageType, type PublicOffer } from "@/lib/offers";
interface PricingCardOfferProps {
  offer?: PublicOffer;
  offerPlacement?: string;
  thankYouCardPromotion?: ThankYouCardPromotionDetails;
}
interface PricingCardsProps {
  offerPlacement?: string;
  offers?: Partial<Record<PlanPackageType, PublicOffer>>;
  thankYouCardPromotion?: ThankYouCardPromotionDetails;
}
interface ThankYouCardPromotionDetails {
  quantity: number;
  offerPlacement: string;
  badge: string;
}
function OfferCallout(t0) {
  const $ = _c(24);
  const {
    offer,
    tone
  } = t0;
  if (!offer) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== offer.badge || $[1] !== offer.benefits || $[2] !== offer.description || $[3] !== offer.headline || $[4] !== tone) {
    const benefitLabels = offer.benefits.map(_temp).filter(_temp2);
    t1 = tone === "dark" ? "rounded-2xl border border-white/15 bg-white/[0.07] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]" : "rounded-2xl border border-amber-200/80 bg-[linear-gradient(135deg,#fffaf0_0%,#fff7e6_58%,#fffdf8_100%)] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]";
    if ($[10] !== offer.badge || $[11] !== tone) {
      t2 = offer.badge ? <p className={tone === "dark" ? "text-[10px] font-semibold uppercase tracking-[0.16em] text-gg-orange-300" : "text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-800"}>{offer.badge}</p> : null;
      $[10] = offer.badge;
      $[11] = tone;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    if ($[13] !== offer.headline) {
      t3 = offer.headline ? <p className="mt-1 text-sm font-semibold leading-5">{offer.headline}</p> : null;
      $[13] = offer.headline;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    if ($[15] !== offer.description || $[16] !== tone) {
      t4 = offer.description ? <p className={tone === "dark" ? "mt-1 text-xs leading-5 text-white/70" : "mt-1 text-xs leading-5 text-stone-600"}>{offer.description}</p> : null;
      $[15] = offer.description;
      $[16] = tone;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t5 = benefitLabels.length > 0 ? <div className="mt-2 flex flex-wrap gap-1.5">{benefitLabels.map(label_0 => <span key={label_0} className={tone === "dark" ? "rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/90" : "rounded-full bg-amber-100/90 px-2.5 py-1 text-[11px] font-medium text-amber-950"}>{label_0}</span>)}</div> : null;
    $[0] = offer.badge;
    $[1] = offer.benefits;
    $[2] = offer.description;
    $[3] = offer.headline;
    $[4] = tone;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className={t1}>{t2}{t3}{t4}{t5}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
function _temp2(label) {
  return Boolean(label);
}
function _temp(benefit) {
  return benefit.label;
}
function getTextFromChildren(children: React.ReactNode): string {
  if (typeof children === "string") return children;
  return React.Children.toArray(children).map(c => typeof c === "string" || typeof c === "number" ? String(c) : "").join(" ").trim();
}
const Feature = t0 => {
  const $ = _c(18);
  const {
    children,
    bold
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== bold || $[1] !== children) {
    const text = getTextFromChildren(children);
    const words = text.split(" ");
    t4 = "flex gap-2 items-center";
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Check className="w-4 h-4" />;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t6 = <span aria-hidden={true} className="sr-only">{text}</span>;
    t1 = true;
    t2 = "inline-block";
    t3 = words.map((word, i) => <span key={`${word}-${i}`} className={bold ? "font-bold" : undefined}>{word}{i < words.length - 1 ? " " : ""}</span>);
    $[0] = bold;
    $[1] = children;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t7 = <span aria-hidden={t1} className={t2}>{t3}</span>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className={t4}>{t5}{t6}{t7}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
const FeatureDate = t0 => {
  const $ = _c(5);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = getTextFromChildren(children);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const text = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CalendarCheck className="w-4 h-4 shrink-0 text-primary" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== text) {
    t3 = <div className="flex gap-2 items-center font-medium">{t2}<span>{text}</span></div>;
    $[3] = text;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
function ThankYouCardsPromotion(t0) {
  const $ = _c(17);
  const {
    promotion
  } = t0;
  const locale = useLocale();
  if (locale !== "da" || !promotion) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute -right-7 -top-7 h-20 w-20 rounded-full border border-[#d8785d]/20" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Gift className="h-3.5 w-3.5" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== promotion.badge) {
    t3 = <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#a64e39]">{t2} {promotion.badge}</p>;
    $[2] = promotion.badge;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== promotion.quantity) {
    t4 = <p className="font-serif text-xl leading-tight tracking-[-0.02em]">{promotion.quantity} gratis takkekort inkluderet</p>;
    $[4] = promotion.quantity;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="shrink-0 rounded-full bg-[#f3dfae] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#745224]">Værdi 240 kr.</span>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2">{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="mt-2 text-xs leading-5 text-[#655d52]">Med et billede efter eget valg og en QR-kode direkte til jeres galleri.</p>;
    t8 = <p className="mt-0.5 text-[11px] font-medium text-[#655d52]">Fragt 49 kr.</p>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-5 text-[#4f493f]"><p>Ekstra takkekort:{" "}<span className="font-bold text-[#29251f]">12 kr. pr. stk.</span></p></div>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex items-center gap-2 text-[#2f4f49]"><ImageIcon className="h-4 w-4" /><QrCode className="h-4 w-4" /></div>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="mt-3 flex items-center justify-between gap-3 border-t border-[#2f4f49]/10 pt-3">{t10}<Link href="/printed-for-you" className="inline-flex items-center gap-1 text-xs font-semibold text-[#2f4f49] underline decoration-[#2f4f49]/30 underline-offset-4 transition-colors hover:text-[#a64e39]">Læs mere <ArrowUpRight className="h-3.5 w-3.5" /></Link></div>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t3 || $[15] !== t6) {
    t12 = <aside className="relative overflow-hidden rounded-2xl border border-[#f4d98b]/45 bg-[#fff8e5] p-4 text-[#29251f] shadow-[0_18px_42px_-28px_rgba(0,0,0,0.65)]">{t1}<div className="relative">{t3}{t6}{t7}{t8}{t9}{t11}</div></aside>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return t12;
}
export function PricingCardLight(t0) {
  const $ = _c(72);
  const {
    offer,
    offerPlacement
  } = t0;
  const t = useTranslations("Pricing.Basic");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CardTitle>Basic</CardTitle>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currencyPrices?.basicPrice || $[5] !== offerPrice || $[6] !== t) {
    t3 = offerPrice ?? currencyPrices?.basicPrice ?? t("price");
    $[4] = currencyPrices?.basicPrice;
    $[5] = offerPrice;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <span className="text-2xl font-bold">{t3}</span>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("PricingCard.pricePostfix");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <span className="text-sm font-medium">{t5}</span>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <p>{t4}{" "}{t6}</p>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("PricingCard.subtitle");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <p>{t8}</p>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t9) {
    t10 = <CardHeader className="text-center flex flex-col gap-4">{t2}<div>{t7}{t9}</div></CardHeader>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== offer) {
    t11 = <OfferCallout offer={offer} tone="light" />;
    $[24] = offer;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("PricingCard.includes");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <p className="">{t12}</p>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <Feature>{t14}</Feature>;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t) {
    t16 = t("PricingCard.EventBenefits.originalQuality");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16) {
    t17 = <Feature>{t16}</Feature>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("PricingCard.EventBenefits.downloadable");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <Feature>{t18}</Feature>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t) {
    t20 = t("PricingCard.EventBenefits.upload");
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <Feature>{t20}</Feature>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("PricingCard.EventBenefits.storage");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== tPricing) {
    t24 = tPricing("chooseDateNote");
    $[50] = tPricing;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t24) {
    t25 = <FeatureDate>{t24}</FeatureDate>;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t15 || $[55] !== t17 || $[56] !== t19 || $[57] !== t21 || $[58] !== t23 || $[59] !== t25) {
    t26 = <div className="flex flex-col gap-2">{t15}{t17}{t19}{t21}{t23}{t25}</div>;
    $[54] = t15;
    $[55] = t17;
    $[56] = t19;
    $[57] = t21;
    $[58] = t23;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== offer || $[62] !== offerPlacement) {
    t27 = <BuyEventButton variant="dark" packageType="basic" offerPlacement={offerPlacement} offer={offer} />;
    $[61] = offer;
    $[62] = offerPlacement;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t11 || $[65] !== t13 || $[66] !== t26 || $[67] !== t27) {
    t28 = <CardContent className="flex flex-col gap-4">{t11}{t13}{t26}{t27}</CardContent>;
    $[64] = t11;
    $[65] = t13;
    $[66] = t26;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t10 || $[70] !== t28) {
    t29 = <Card className="bg-gg-orange-100 text-dark flex flex-col justify-between">{t10}{t28}</Card>;
    $[69] = t10;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  return t29;
}
export function PricingCardLightChristmas(t0) {
  const $ = _c(67);
  const {
    offer,
    offerPlacement
  } = t0;
  const t = useTranslations("Pricing.Basic");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CardTitle>Basic</CardTitle>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currencyPrices?.basicPrice || $[5] !== offerPrice || $[6] !== t) {
    t3 = offerPrice ?? currencyPrices?.basicPrice ?? t("price");
    $[4] = currencyPrices?.basicPrice;
    $[5] = offerPrice;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <span className="text-2xl font-bold">{t3}</span>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("PricingCard.pricePostfix");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <span className="text-sm font-medium">{t5}</span>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <p>{t4}{" "}{t6}</p>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("PricingCard.subtitle");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <p>{t8}</p>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t9) {
    t10 = <CardHeader className="text-center flex flex-col gap-4">{t2}<div>{t7}{t9}</div></CardHeader>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== offer) {
    t11 = <OfferCallout offer={offer} tone="light" />;
    $[24] = offer;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("PricingCard.includes");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <p className="">{t12}</p>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <Feature>{t14}</Feature>;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t) {
    t16 = t("PricingCard.EventBenefits.originalQuality");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16) {
    t17 = <Feature>{t16}</Feature>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("PricingCard.EventBenefits.downloadable");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <Feature>{t18}</Feature>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t) {
    t20 = t("PricingCard.EventBenefits.upload");
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <Feature>{t20}</Feature>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("PricingCard.EventBenefits.storage");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t15 || $[51] !== t17 || $[52] !== t19 || $[53] !== t21 || $[54] !== t23) {
    t24 = <div className="flex flex-col gap-2">{t15}{t17}{t19}{t21}{t23}</div>;
    $[50] = t15;
    $[51] = t17;
    $[52] = t19;
    $[53] = t21;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== offer || $[57] !== offerPlacement) {
    t25 = <BuyEventButton variant="christmas" packageType="basic" offerPlacement={offerPlacement} offer={offer} />;
    $[56] = offer;
    $[57] = offerPlacement;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t11 || $[60] !== t13 || $[61] !== t24 || $[62] !== t25) {
    t26 = <CardContent className="flex flex-col gap-4">{t11}{t13}{t24}{t25}</CardContent>;
    $[59] = t11;
    $[60] = t13;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t10 || $[65] !== t26) {
    t27 = <Card className="bg-white text-dark flex flex-col justify-between">{t10}{t26}</Card>;
    $[64] = t10;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  return t27;
}
export function PricingCardDark(t0) {
  const $ = _c(92);
  const {
    offer,
    offerPlacement,
    thankYouCardPromotion
  } = t0;
  const t = useTranslations("Pricing.Premium");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("PricingCard.rebate");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== currencyPrices?.rebateAmount || $[6] !== t) {
    t3 = currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount");
    $[5] = currencyPrices?.rebateAmount;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <span className="underline">{t3}</span>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="absolute top-0 right-0 rounded-l-full bg-[#373843] text-gg-orange-300 p-1 py-2 pl-3 text-xs font-bold space-y-1.5 mt-6"><p>{t2}{" "}{t4}.</p></div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("PricingCard.title");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <CardTitle>{t6}</CardTitle>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== currencyPrices?.premiumPrice || $[18] !== offerPrice || $[19] !== t) {
    t8 = offerPrice ?? currencyPrices?.premiumPrice ?? t("price");
    $[17] = currencyPrices?.premiumPrice;
    $[18] = offerPrice;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <span className="text-2xl font-bold">{t8}</span>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("PricingCard.pricePostfix");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <span className="text-sm font-medium">{t10}</span>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t9) {
    t12 = <p>{t9}{" "}{t11}</p>;
    $[27] = t11;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = t("PricingCard.subtitle");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <p className="text-white/90">{t13}</p>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <div>{t12}{t14}</div>;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t15 || $[38] !== t7) {
    t16 = <CardHeader className="text-center flex flex-col gap-4">{t7}{t15}</CardHeader>;
    $[37] = t15;
    $[38] = t7;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== offer) {
    t17 = <OfferCallout offer={offer} tone="dark" />;
    $[40] = offer;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t) {
    t18 = t("PricingCard.includes");
    $[42] = t;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t18) {
    t19 = <p>{t18}</p>;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t) {
    t20 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[46] = t;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20) {
    t21 = <Feature bold={true}>{t20}</Feature>;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t) {
    t22 = t("PricingCard.EventBenefits.originalQuality");
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t) {
    t24 = t("PricingCard.EventBenefits.downloadable");
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t) {
    t26 = t("PricingCard.EventBenefits.upload");
    $[58] = t;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t26) {
    t27 = <Feature bold={true}>{t26}</Feature>;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t) {
    t28 = t("PricingCard.EventBenefits.storage");
    $[62] = t;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t28) {
    t29 = <Feature bold={true}>{t28}</Feature>;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== tPricing) {
    t30 = tPricing("chooseDateNote");
    $[66] = tPricing;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== t30) {
    t31 = <FeatureDate>{t30}</FeatureDate>;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t21 || $[71] !== t23 || $[72] !== t25 || $[73] !== t27 || $[74] !== t29 || $[75] !== t31) {
    t32 = <div className="flex flex-col gap-2">{t21}{t23}{t25}{t27}{t29}{t31}</div>;
    $[70] = t21;
    $[71] = t23;
    $[72] = t25;
    $[73] = t27;
    $[74] = t29;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== thankYouCardPromotion) {
    t33 = <ThankYouCardsPromotion promotion={thankYouCardPromotion} />;
    $[77] = thankYouCardPromotion;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  const t34 = thankYouCardPromotion?.offerPlacement ?? offerPlacement;
  let t35;
  if ($[79] !== offer || $[80] !== t34) {
    t35 = <BuyEventButton variant="lightest" packageType="premium" offerPlacement={t34} offer={offer} />;
    $[79] = offer;
    $[80] = t34;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== t17 || $[83] !== t19 || $[84] !== t32 || $[85] !== t33 || $[86] !== t35) {
    t36 = <CardContent className="flex flex-col gap-4">{t17}{t19}{t32}{t33}{t35}</CardContent>;
    $[82] = t17;
    $[83] = t19;
    $[84] = t32;
    $[85] = t33;
    $[86] = t35;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== t16 || $[89] !== t36 || $[90] !== t5) {
    t37 = <Card className="bg-dark text-white relative flex flex-col justify-between">{t5}{t16}{t36}</Card>;
    $[88] = t16;
    $[89] = t36;
    $[90] = t5;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  return t37;
}
export function PricingCardDarkChristmas(t0) {
  const $ = _c(87);
  const {
    offer,
    offerPlacement,
    thankYouCardPromotion
  } = t0;
  const t = useTranslations("Pricing.Premium");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("PricingCard.rebate");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== currencyPrices?.rebateAmount || $[6] !== t) {
    t3 = currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount");
    $[5] = currencyPrices?.rebateAmount;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <span className="underline">{t3}</span>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="absolute top-0 right-0 rounded-l-full bg-gg-christmas-red p-1 py-2 pl-3 text-xs font-bold space-y-1.5 mt-6"><p>{t2}{" "}{t4}.</p></div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("PricingCard.title");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <CardTitle>{t6}</CardTitle>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== currencyPrices?.premiumPrice || $[18] !== offerPrice || $[19] !== t) {
    t8 = offerPrice ?? currencyPrices?.premiumPrice ?? t("price");
    $[17] = currencyPrices?.premiumPrice;
    $[18] = offerPrice;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <span className="text-2xl font-bold">{t8}</span>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("PricingCard.pricePostfix");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <span className="text-sm font-medium">{t10}</span>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t9) {
    t12 = <p>{t9}{" "}{t11}</p>;
    $[27] = t11;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = t("PricingCard.subtitle");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <p className="text-white/90">{t13}</p>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <div>{t12}{t14}</div>;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t15 || $[38] !== t7) {
    t16 = <CardHeader className="text-center flex flex-col gap-4">{t7}{t15}</CardHeader>;
    $[37] = t15;
    $[38] = t7;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== offer) {
    t17 = <OfferCallout offer={offer} tone="dark" />;
    $[40] = offer;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t) {
    t18 = t("PricingCard.includes");
    $[42] = t;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t18) {
    t19 = <p>{t18}</p>;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t) {
    t20 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[46] = t;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20) {
    t21 = <Feature bold={true}>{t20}</Feature>;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t) {
    t22 = t("PricingCard.EventBenefits.originalQuality");
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t) {
    t24 = t("PricingCard.EventBenefits.downloadable");
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t) {
    t26 = t("PricingCard.EventBenefits.upload");
    $[58] = t;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t26) {
    t27 = <Feature bold={true}>{t26}</Feature>;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t) {
    t28 = t("PricingCard.EventBenefits.storage");
    $[62] = t;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t28) {
    t29 = <Feature bold={true}>{t28}</Feature>;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t21 || $[67] !== t23 || $[68] !== t25 || $[69] !== t27 || $[70] !== t29) {
    t30 = <div className="flex flex-col gap-2">{t21}{t23}{t25}{t27}{t29}</div>;
    $[66] = t21;
    $[67] = t23;
    $[68] = t25;
    $[69] = t27;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== thankYouCardPromotion) {
    t31 = <ThankYouCardsPromotion promotion={thankYouCardPromotion} />;
    $[72] = thankYouCardPromotion;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  const t32 = thankYouCardPromotion?.offerPlacement ?? offerPlacement;
  let t33;
  if ($[74] !== offer || $[75] !== t32) {
    t33 = <BuyEventButton variant="white" packageType="premium" offerPlacement={t32} offer={offer} />;
    $[74] = offer;
    $[75] = t32;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] !== t17 || $[78] !== t19 || $[79] !== t30 || $[80] !== t31 || $[81] !== t33) {
    t34 = <CardContent className="flex flex-col gap-4">{t17}{t19}{t30}{t31}{t33}</CardContent>;
    $[77] = t17;
    $[78] = t19;
    $[79] = t30;
    $[80] = t31;
    $[81] = t33;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] !== t16 || $[84] !== t34 || $[85] !== t5) {
    t35 = <Card className="bg-dark text-white relative flex flex-col justify-between">{t5}{t16}{t34}</Card>;
    $[83] = t16;
    $[84] = t34;
    $[85] = t5;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  return t35;
}
export function PricingCards(t0) {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offerPlacement,
    offers,
    thankYouCardPromotion
  } = t1;
  const t = useTranslations("Pricing");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DetectedLocalPricingNudge className="mb-1 self-center" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = offers?.basic;
  let t4;
  if ($[3] !== offerPlacement || $[4] !== t3) {
    t4 = <PricingCardLight offer={t3} offerPlacement={offerPlacement} />;
    $[3] = offerPlacement;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = offers?.premium;
  let t6;
  if ($[6] !== offerPlacement || $[7] !== t5 || $[8] !== thankYouCardPromotion) {
    t6 = <PricingCardDark offer={t5} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} />;
    $[6] = offerPlacement;
    $[7] = t5;
    $[8] = thankYouCardPromotion;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t6) {
    t7 = <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">{t4}{t6}</div>;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("fairUsePolicy");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Link href="/terms-and-conditions" className="underline" target="_blank">fair use policy</Link>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8) {
    t10 = <p className="text-xs text-dark/50">*{t8}{" "}{t9}.</p>;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7) {
    t11 = <div className="flex flex-col gap-2 items-end">{t2}{t7}{t10}</div>;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export function PricingCardBasicKonfirmation(t0) {
  const $ = _c(74);
  const {
    offer,
    offerPlacement
  } = t0;
  const t = useTranslations("Pricing.Basic");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#F5EDE8",
      color: "#3A2C24"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CardTitle>Basic</CardTitle>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== currencyPrices?.basicPrice || $[6] !== offerPrice || $[7] !== t) {
    t4 = offerPrice ?? currencyPrices?.basicPrice ?? t("price");
    $[5] = currencyPrices?.basicPrice;
    $[6] = offerPrice;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-2xl font-bold">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("PricingCard.pricePostfix");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-sm font-medium">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <p>{t5}{" "}{t7}</p>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      color: "#5C473A"
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("PricingCard.subtitle");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <p style={t9}>{t10}</p>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <CardHeader className="text-center flex flex-col gap-4">{t3}<div>{t8}{t11}</div></CardHeader>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== offer) {
    t13 = <OfferCallout offer={offer} tone="light" />;
    $[26] = offer;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("PricingCard.includes");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <p>{t14}</p>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <Feature>{t16}</Feature>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t("PricingCard.EventBenefits.originalQuality");
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <Feature>{t18}</Feature>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("PricingCard.EventBenefits.downloadable");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = <Feature>{t20}</Feature>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t) {
    t22 = t("PricingCard.EventBenefits.upload");
    $[44] = t;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t) {
    t24 = t("PricingCard.EventBenefits.storage");
    $[48] = t;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== tPricing) {
    t26 = tPricing("chooseDateNote");
    $[52] = tPricing;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t26) {
    t27 = <FeatureDate>{t26}</FeatureDate>;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t17 || $[57] !== t19 || $[58] !== t21 || $[59] !== t23 || $[60] !== t25 || $[61] !== t27) {
    t28 = <div className="flex flex-col gap-2">{t17}{t19}{t21}{t23}{t25}{t27}</div>;
    $[56] = t17;
    $[57] = t19;
    $[58] = t21;
    $[59] = t23;
    $[60] = t25;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== offer || $[64] !== offerPlacement) {
    t29 = <BuyEventButton variant="konfirmation" packageType="basic" offerPlacement={offerPlacement} offer={offer} />;
    $[63] = offer;
    $[64] = offerPlacement;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t13 || $[67] !== t15 || $[68] !== t28 || $[69] !== t29) {
    t30 = <CardContent className="flex flex-col gap-4">{t13}{t15}{t28}{t29}</CardContent>;
    $[66] = t13;
    $[67] = t15;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t12 || $[72] !== t30) {
    t31 = <Card className="flex flex-col justify-between border-none" style={t2}>{t12}{t30}</Card>;
    $[71] = t12;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  return t31;
}
export function PricingCardPremiumKonfirmation(t0) {
  const $ = _c(94);
  const {
    offer,
    offerPlacement,
    thankYouCardPromotion
  } = t0;
  const t = useTranslations("Pricing.Premium");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#3A2C24"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "#B08E7E",
      color: "white"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("PricingCard.rebate");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== currencyPrices?.rebateAmount || $[8] !== t) {
    t5 = currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount");
    $[7] = currencyPrices?.rebateAmount;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="underline">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="absolute top-0 right-0 rounded-l-full p-1 py-2 pl-3 text-xs font-bold space-y-1.5 mt-6" style={t3}><p>{t4}{" "}{t6}.</p></div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("PricingCard.title");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <CardTitle>{t8}</CardTitle>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== currencyPrices?.premiumPrice || $[20] !== offerPrice || $[21] !== t) {
    t10 = offerPrice ?? currencyPrices?.premiumPrice ?? t("price");
    $[19] = currencyPrices?.premiumPrice;
    $[20] = offerPrice;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <span className="text-2xl font-bold">{t10}</span>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("PricingCard.pricePostfix");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <span className="text-sm font-medium">{t12}</span>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <p>{t11}{" "}{t13}</p>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("PricingCard.subtitle");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <p className="text-white/90">{t15}</p>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = <div>{t14}{t16}</div>;
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t9) {
    t18 = <CardHeader className="text-center flex flex-col gap-4">{t9}{t17}</CardHeader>;
    $[39] = t17;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== offer) {
    t19 = <OfferCallout offer={offer} tone="dark" />;
    $[42] = offer;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t) {
    t20 = t("PricingCard.includes");
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <p>{t20}</p>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22) {
    t23 = <Feature bold={true}>{t22}</Feature>;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("PricingCard.EventBenefits.originalQuality");
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("PricingCard.EventBenefits.downloadable");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <Feature>{t26}</Feature>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t("PricingCard.EventBenefits.upload");
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t28) {
    t29 = <Feature bold={true}>{t28}</Feature>;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t) {
    t30 = t("PricingCard.EventBenefits.storage");
    $[64] = t;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t30) {
    t31 = <Feature bold={true}>{t30}</Feature>;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== tPricing) {
    t32 = tPricing("chooseDateNote");
    $[68] = tPricing;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t32) {
    t33 = <FeatureDate>{t32}</FeatureDate>;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t23 || $[73] !== t25 || $[74] !== t27 || $[75] !== t29 || $[76] !== t31 || $[77] !== t33) {
    t34 = <div className="flex flex-col gap-2">{t23}{t25}{t27}{t29}{t31}{t33}</div>;
    $[72] = t23;
    $[73] = t25;
    $[74] = t27;
    $[75] = t29;
    $[76] = t31;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== thankYouCardPromotion) {
    t35 = <ThankYouCardsPromotion promotion={thankYouCardPromotion} />;
    $[79] = thankYouCardPromotion;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  const t36 = thankYouCardPromotion?.offerPlacement ?? offerPlacement;
  let t37;
  if ($[81] !== offer || $[82] !== t36) {
    t37 = <BuyEventButton variant="white" packageType="premium" offerPlacement={t36} offer={offer} />;
    $[81] = offer;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t19 || $[85] !== t21 || $[86] !== t34 || $[87] !== t35 || $[88] !== t37) {
    t38 = <CardContent className="flex flex-col gap-4">{t19}{t21}{t34}{t35}{t37}</CardContent>;
    $[84] = t19;
    $[85] = t21;
    $[86] = t34;
    $[87] = t35;
    $[88] = t37;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t18 || $[91] !== t38 || $[92] !== t7) {
    t39 = <Card className="relative flex flex-col justify-between border-none text-white" style={t2}>{t7}{t18}{t38}</Card>;
    $[90] = t18;
    $[91] = t38;
    $[92] = t7;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  return t39;
}
export function PricingCardsKonfirmation(t0) {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offerPlacement,
    offers,
    thankYouCardPromotion
  } = t1;
  const t = useTranslations("Pricing");
  const t2 = offers?.basic;
  let t3;
  if ($[2] !== offerPlacement || $[3] !== t2) {
    t3 = <PricingCardBasicKonfirmation offer={t2} offerPlacement={offerPlacement} />;
    $[2] = offerPlacement;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = offers?.premium;
  let t5;
  if ($[5] !== offerPlacement || $[6] !== t4 || $[7] !== thankYouCardPromotion) {
    t5 = <PricingCardPremiumKonfirmation offer={t4} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} />;
    $[5] = offerPlacement;
    $[6] = t4;
    $[7] = thankYouCardPromotion;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      color: "rgba(58, 44, 36, 0.5)"
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("fairUsePolicy");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Link href="/terms-and-conditions" className="underline" target="_blank">fair use policy</Link>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8) {
    t10 = <p className="text-xs" style={t7}>*{t8}{" "}{t9}.</p>;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <div className="flex flex-col gap-2 items-end">{t6}{t10}</div>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export function PricingCardBasicBarnedab(t0) {
  const $ = _c(74);
  const {
    offer,
    offerPlacement
  } = t0;
  const t = useTranslations("Pricing.Basic");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#F0EBF8",
      color: "#2D1D42"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CardTitle>Basic</CardTitle>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== currencyPrices?.basicPrice || $[6] !== offerPrice || $[7] !== t) {
    t4 = offerPrice ?? currencyPrices?.basicPrice ?? t("price");
    $[5] = currencyPrices?.basicPrice;
    $[6] = offerPrice;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-2xl font-bold">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("PricingCard.pricePostfix");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-sm font-medium">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <p>{t5}{" "}{t7}</p>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      color: "#5C4278"
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("PricingCard.subtitle");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <p style={t9}>{t10}</p>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <CardHeader className="text-center flex flex-col gap-4">{t3}<div>{t8}{t11}</div></CardHeader>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== offer) {
    t13 = <OfferCallout offer={offer} tone="light" />;
    $[26] = offer;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("PricingCard.includes");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <p>{t14}</p>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <Feature>{t16}</Feature>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t("PricingCard.EventBenefits.originalQuality");
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <Feature>{t18}</Feature>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("PricingCard.EventBenefits.downloadable");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = <Feature>{t20}</Feature>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t) {
    t22 = t("PricingCard.EventBenefits.upload");
    $[44] = t;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t) {
    t24 = t("PricingCard.EventBenefits.storage");
    $[48] = t;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== tPricing) {
    t26 = tPricing("chooseDateNote");
    $[52] = tPricing;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t26) {
    t27 = <FeatureDate>{t26}</FeatureDate>;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t17 || $[57] !== t19 || $[58] !== t21 || $[59] !== t23 || $[60] !== t25 || $[61] !== t27) {
    t28 = <div className="flex flex-col gap-2">{t17}{t19}{t21}{t23}{t25}{t27}</div>;
    $[56] = t17;
    $[57] = t19;
    $[58] = t21;
    $[59] = t23;
    $[60] = t25;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== offer || $[64] !== offerPlacement) {
    t29 = <BuyEventButton variant="barnedab" packageType="basic" offerPlacement={offerPlacement} offer={offer} />;
    $[63] = offer;
    $[64] = offerPlacement;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t13 || $[67] !== t15 || $[68] !== t28 || $[69] !== t29) {
    t30 = <CardContent className="flex flex-col gap-4">{t13}{t15}{t28}{t29}</CardContent>;
    $[66] = t13;
    $[67] = t15;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t12 || $[72] !== t30) {
    t31 = <Card className="flex flex-col justify-between border-none" style={t2}>{t12}{t30}</Card>;
    $[71] = t12;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  return t31;
}
export function PricingCardPremiumBarnedab(t0) {
  const $ = _c(94);
  const {
    offer,
    offerPlacement,
    thankYouCardPromotion
  } = t0;
  const t = useTranslations("Pricing.Premium");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#3D2858"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "#8B72A8",
      color: "white"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("PricingCard.rebate");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== currencyPrices?.rebateAmount || $[8] !== t) {
    t5 = currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount");
    $[7] = currencyPrices?.rebateAmount;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="underline">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="absolute top-0 right-0 rounded-l-full p-1 py-2 pl-3 text-xs font-bold space-y-1.5 mt-6" style={t3}><p>{t4}{" "}{t6}.</p></div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("PricingCard.title");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <CardTitle>{t8}</CardTitle>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== currencyPrices?.premiumPrice || $[20] !== offerPrice || $[21] !== t) {
    t10 = offerPrice ?? currencyPrices?.premiumPrice ?? t("price");
    $[19] = currencyPrices?.premiumPrice;
    $[20] = offerPrice;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <span className="text-2xl font-bold">{t10}</span>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("PricingCard.pricePostfix");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <span className="text-sm font-medium">{t12}</span>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <p>{t11}{" "}{t13}</p>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("PricingCard.subtitle");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <p className="text-white/90">{t15}</p>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = <div>{t14}{t16}</div>;
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t9) {
    t18 = <CardHeader className="text-center flex flex-col gap-4">{t9}{t17}</CardHeader>;
    $[39] = t17;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== offer) {
    t19 = <OfferCallout offer={offer} tone="dark" />;
    $[42] = offer;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t) {
    t20 = t("PricingCard.includes");
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <p>{t20}</p>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22) {
    t23 = <Feature bold={true}>{t22}</Feature>;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("PricingCard.EventBenefits.originalQuality");
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("PricingCard.EventBenefits.downloadable");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <Feature>{t26}</Feature>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t("PricingCard.EventBenefits.upload");
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t28) {
    t29 = <Feature bold={true}>{t28}</Feature>;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t) {
    t30 = t("PricingCard.EventBenefits.storage");
    $[64] = t;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t30) {
    t31 = <Feature bold={true}>{t30}</Feature>;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== tPricing) {
    t32 = tPricing("chooseDateNote");
    $[68] = tPricing;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t32) {
    t33 = <FeatureDate>{t32}</FeatureDate>;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t23 || $[73] !== t25 || $[74] !== t27 || $[75] !== t29 || $[76] !== t31 || $[77] !== t33) {
    t34 = <div className="flex flex-col gap-2">{t23}{t25}{t27}{t29}{t31}{t33}</div>;
    $[72] = t23;
    $[73] = t25;
    $[74] = t27;
    $[75] = t29;
    $[76] = t31;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== thankYouCardPromotion) {
    t35 = <ThankYouCardsPromotion promotion={thankYouCardPromotion} />;
    $[79] = thankYouCardPromotion;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  const t36 = thankYouCardPromotion?.offerPlacement ?? offerPlacement;
  let t37;
  if ($[81] !== offer || $[82] !== t36) {
    t37 = <BuyEventButton variant="white" packageType="premium" offerPlacement={t36} offer={offer} />;
    $[81] = offer;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t19 || $[85] !== t21 || $[86] !== t34 || $[87] !== t35 || $[88] !== t37) {
    t38 = <CardContent className="flex flex-col gap-4">{t19}{t21}{t34}{t35}{t37}</CardContent>;
    $[84] = t19;
    $[85] = t21;
    $[86] = t34;
    $[87] = t35;
    $[88] = t37;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t18 || $[91] !== t38 || $[92] !== t7) {
    t39 = <Card className="relative flex flex-col justify-between border-none text-white" style={t2}>{t7}{t18}{t38}</Card>;
    $[90] = t18;
    $[91] = t38;
    $[92] = t7;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  return t39;
}
export function PricingCardsBarnedab(t0) {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offerPlacement,
    offers,
    thankYouCardPromotion
  } = t1;
  const t = useTranslations("Pricing");
  const t2 = offers?.basic;
  let t3;
  if ($[2] !== offerPlacement || $[3] !== t2) {
    t3 = <PricingCardBasicBarnedab offer={t2} offerPlacement={offerPlacement} />;
    $[2] = offerPlacement;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = offers?.premium;
  let t5;
  if ($[5] !== offerPlacement || $[6] !== t4 || $[7] !== thankYouCardPromotion) {
    t5 = <PricingCardPremiumBarnedab offer={t4} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} />;
    $[5] = offerPlacement;
    $[6] = t4;
    $[7] = thankYouCardPromotion;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      color: "rgba(45, 29, 66, 0.5)"
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("fairUsePolicy");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Link href="/terms-and-conditions" className="underline" target="_blank">fair use policy</Link>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8) {
    t10 = <p className="text-xs" style={t7}>*{t8}{" "}{t9}.</p>;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <div className="flex flex-col gap-2 items-end">{t6}{t10}</div>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export function PricingCardBasicFodselsdag(t0) {
  const $ = _c(74);
  const {
    offer,
    offerPlacement
  } = t0;
  const t = useTranslations("Pricing.Basic");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#FBF5E6",
      color: "#3A2A08"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CardTitle>Basic</CardTitle>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== currencyPrices?.basicPrice || $[6] !== offerPrice || $[7] !== t) {
    t4 = offerPrice ?? currencyPrices?.basicPrice ?? t("price");
    $[5] = currencyPrices?.basicPrice;
    $[6] = offerPrice;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-2xl font-bold">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("PricingCard.pricePostfix");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-sm font-medium">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <p>{t5}{" "}{t7}</p>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      color: "#6B5016"
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("PricingCard.subtitle");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <p style={t9}>{t10}</p>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <CardHeader className="text-center flex flex-col gap-4">{t3}<div>{t8}{t11}</div></CardHeader>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== offer) {
    t13 = <OfferCallout offer={offer} tone="light" />;
    $[26] = offer;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("PricingCard.includes");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <p>{t14}</p>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <Feature>{t16}</Feature>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t("PricingCard.EventBenefits.originalQuality");
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <Feature>{t18}</Feature>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("PricingCard.EventBenefits.downloadable");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = <Feature>{t20}</Feature>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t) {
    t22 = t("PricingCard.EventBenefits.upload");
    $[44] = t;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t) {
    t24 = t("PricingCard.EventBenefits.storage");
    $[48] = t;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== tPricing) {
    t26 = tPricing("chooseDateNote");
    $[52] = tPricing;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t26) {
    t27 = <FeatureDate>{t26}</FeatureDate>;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t17 || $[57] !== t19 || $[58] !== t21 || $[59] !== t23 || $[60] !== t25 || $[61] !== t27) {
    t28 = <div className="flex flex-col gap-2">{t17}{t19}{t21}{t23}{t25}{t27}</div>;
    $[56] = t17;
    $[57] = t19;
    $[58] = t21;
    $[59] = t23;
    $[60] = t25;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== offer || $[64] !== offerPlacement) {
    t29 = <BuyEventButton variant="fodselsdag" packageType="basic" offerPlacement={offerPlacement} offer={offer} />;
    $[63] = offer;
    $[64] = offerPlacement;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t13 || $[67] !== t15 || $[68] !== t28 || $[69] !== t29) {
    t30 = <CardContent className="flex flex-col gap-4">{t13}{t15}{t28}{t29}</CardContent>;
    $[66] = t13;
    $[67] = t15;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t12 || $[72] !== t30) {
    t31 = <Card className="flex flex-col justify-between border-none" style={t2}>{t12}{t30}</Card>;
    $[71] = t12;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  return t31;
}
export function PricingCardPremiumFodselsdag(t0) {
  const $ = _c(94);
  const {
    offer,
    offerPlacement,
    thankYouCardPromotion
  } = t0;
  const t = useTranslations("Pricing.Premium");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#3A2A08"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "#8C6D1C",
      color: "white"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("PricingCard.rebate");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== currencyPrices?.rebateAmount || $[8] !== t) {
    t5 = currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount");
    $[7] = currencyPrices?.rebateAmount;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="underline">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="absolute top-0 right-0 rounded-l-full p-1 py-2 pl-3 text-xs font-bold space-y-1.5 mt-6" style={t3}><p>{t4}{" "}{t6}.</p></div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("PricingCard.title");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <CardTitle>{t8}</CardTitle>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== currencyPrices?.premiumPrice || $[20] !== offerPrice || $[21] !== t) {
    t10 = offerPrice ?? currencyPrices?.premiumPrice ?? t("price");
    $[19] = currencyPrices?.premiumPrice;
    $[20] = offerPrice;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <span className="text-2xl font-bold">{t10}</span>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("PricingCard.pricePostfix");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <span className="text-sm font-medium">{t12}</span>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <p>{t11}{" "}{t13}</p>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("PricingCard.subtitle");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <p className="text-white/90">{t15}</p>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = <div>{t14}{t16}</div>;
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t9) {
    t18 = <CardHeader className="text-center flex flex-col gap-4">{t9}{t17}</CardHeader>;
    $[39] = t17;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== offer) {
    t19 = <OfferCallout offer={offer} tone="dark" />;
    $[42] = offer;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t) {
    t20 = t("PricingCard.includes");
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <p>{t20}</p>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22) {
    t23 = <Feature bold={true}>{t22}</Feature>;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("PricingCard.EventBenefits.originalQuality");
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("PricingCard.EventBenefits.downloadable");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <Feature>{t26}</Feature>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t("PricingCard.EventBenefits.upload");
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t28) {
    t29 = <Feature bold={true}>{t28}</Feature>;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t) {
    t30 = t("PricingCard.EventBenefits.storage");
    $[64] = t;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t30) {
    t31 = <Feature bold={true}>{t30}</Feature>;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== tPricing) {
    t32 = tPricing("chooseDateNote");
    $[68] = tPricing;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t32) {
    t33 = <FeatureDate>{t32}</FeatureDate>;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t23 || $[73] !== t25 || $[74] !== t27 || $[75] !== t29 || $[76] !== t31 || $[77] !== t33) {
    t34 = <div className="flex flex-col gap-2">{t23}{t25}{t27}{t29}{t31}{t33}</div>;
    $[72] = t23;
    $[73] = t25;
    $[74] = t27;
    $[75] = t29;
    $[76] = t31;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== thankYouCardPromotion) {
    t35 = <ThankYouCardsPromotion promotion={thankYouCardPromotion} />;
    $[79] = thankYouCardPromotion;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  const t36 = thankYouCardPromotion?.offerPlacement ?? offerPlacement;
  let t37;
  if ($[81] !== offer || $[82] !== t36) {
    t37 = <BuyEventButton variant="white" packageType="premium" offerPlacement={t36} offer={offer} />;
    $[81] = offer;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t19 || $[85] !== t21 || $[86] !== t34 || $[87] !== t35 || $[88] !== t37) {
    t38 = <CardContent className="flex flex-col gap-4">{t19}{t21}{t34}{t35}{t37}</CardContent>;
    $[84] = t19;
    $[85] = t21;
    $[86] = t34;
    $[87] = t35;
    $[88] = t37;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t18 || $[91] !== t38 || $[92] !== t7) {
    t39 = <Card className="relative flex flex-col justify-between border-none text-white" style={t2}>{t7}{t18}{t38}</Card>;
    $[90] = t18;
    $[91] = t38;
    $[92] = t7;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  return t39;
}
export function PricingCardsFodselsdag(t0) {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offerPlacement,
    offers,
    thankYouCardPromotion
  } = t1;
  const t = useTranslations("Pricing");
  const t2 = offers?.basic;
  let t3;
  if ($[2] !== offerPlacement || $[3] !== t2) {
    t3 = <PricingCardBasicFodselsdag offer={t2} offerPlacement={offerPlacement} />;
    $[2] = offerPlacement;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = offers?.premium;
  let t5;
  if ($[5] !== offerPlacement || $[6] !== t4 || $[7] !== thankYouCardPromotion) {
    t5 = <PricingCardPremiumFodselsdag offer={t4} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} />;
    $[5] = offerPlacement;
    $[6] = t4;
    $[7] = thankYouCardPromotion;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      color: "rgba(58, 42, 8, 0.5)"
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("fairUsePolicy");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Link href="/terms-and-conditions" className="underline" target="_blank">fair use policy</Link>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8) {
    t10 = <p className="text-xs" style={t7}>*{t8}{" "}{t9}.</p>;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <div className="flex flex-col gap-2 items-end">{t6}{t10}</div>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export function PricingCardBasicBarMitsva(t0) {
  const $ = _c(74);
  const {
    offer,
    offerPlacement
  } = t0;
  const t = useTranslations("Pricing.Basic");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#E8EEF8",
      color: "#1A2A50"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CardTitle>Basic</CardTitle>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== currencyPrices?.basicPrice || $[6] !== offerPrice || $[7] !== t) {
    t4 = offerPrice ?? currencyPrices?.basicPrice ?? t("price");
    $[5] = currencyPrices?.basicPrice;
    $[6] = offerPrice;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-2xl font-bold">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("PricingCard.pricePostfix");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-sm font-medium">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <p>{t5}{" "}{t7}</p>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      color: "#2C3D6A"
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("PricingCard.subtitle");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <p style={t9}>{t10}</p>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <CardHeader className="text-center flex flex-col gap-4">{t3}<div>{t8}{t11}</div></CardHeader>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== offer) {
    t13 = <OfferCallout offer={offer} tone="light" />;
    $[26] = offer;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("PricingCard.includes");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <p>{t14}</p>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <Feature>{t16}</Feature>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t("PricingCard.EventBenefits.originalQuality");
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <Feature>{t18}</Feature>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("PricingCard.EventBenefits.downloadable");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = <Feature>{t20}</Feature>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t) {
    t22 = t("PricingCard.EventBenefits.upload");
    $[44] = t;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t22) {
    t23 = <Feature>{t22}</Feature>;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t) {
    t24 = t("PricingCard.EventBenefits.storage");
    $[48] = t;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== tPricing) {
    t26 = tPricing("chooseDateNote");
    $[52] = tPricing;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t26) {
    t27 = <FeatureDate>{t26}</FeatureDate>;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t17 || $[57] !== t19 || $[58] !== t21 || $[59] !== t23 || $[60] !== t25 || $[61] !== t27) {
    t28 = <div className="flex flex-col gap-2">{t17}{t19}{t21}{t23}{t25}{t27}</div>;
    $[56] = t17;
    $[57] = t19;
    $[58] = t21;
    $[59] = t23;
    $[60] = t25;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== offer || $[64] !== offerPlacement) {
    t29 = <BuyEventButton variant="barMitsva" packageType="basic" offerPlacement={offerPlacement} offer={offer} />;
    $[63] = offer;
    $[64] = offerPlacement;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t13 || $[67] !== t15 || $[68] !== t28 || $[69] !== t29) {
    t30 = <CardContent className="flex flex-col gap-4">{t13}{t15}{t28}{t29}</CardContent>;
    $[66] = t13;
    $[67] = t15;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t12 || $[72] !== t30) {
    t31 = <Card className="flex flex-col justify-between border-none" style={t2}>{t12}{t30}</Card>;
    $[71] = t12;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  return t31;
}
export function PricingCardPremiumBarMitsva(t0) {
  const $ = _c(94);
  const {
    offer,
    offerPlacement,
    thankYouCardPromotion
  } = t0;
  const t = useTranslations("Pricing.Premium");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#1A2A50"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "#2C4A8C",
      color: "white"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("PricingCard.rebate");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== currencyPrices?.rebateAmount || $[8] !== t) {
    t5 = currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount");
    $[7] = currencyPrices?.rebateAmount;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="underline">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="absolute top-0 right-0 rounded-l-full p-1 py-2 pl-3 text-xs font-bold space-y-1.5 mt-6" style={t3}><p>{t4}{" "}{t6}.</p></div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("PricingCard.title");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <CardTitle>{t8}</CardTitle>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== currencyPrices?.premiumPrice || $[20] !== offerPrice || $[21] !== t) {
    t10 = offerPrice ?? currencyPrices?.premiumPrice ?? t("price");
    $[19] = currencyPrices?.premiumPrice;
    $[20] = offerPrice;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <span className="text-2xl font-bold">{t10}</span>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("PricingCard.pricePostfix");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <span className="text-sm font-medium">{t12}</span>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <p>{t11}{" "}{t13}</p>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("PricingCard.subtitle");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <p className="text-white/90">{t15}</p>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = <div>{t14}{t16}</div>;
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t9) {
    t18 = <CardHeader className="text-center flex flex-col gap-4">{t9}{t17}</CardHeader>;
    $[39] = t17;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== offer) {
    t19 = <OfferCallout offer={offer} tone="dark" />;
    $[42] = offer;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t) {
    t20 = t("PricingCard.includes");
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <p>{t20}</p>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22) {
    t23 = <Feature bold={true}>{t22}</Feature>;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("PricingCard.EventBenefits.originalQuality");
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24) {
    t25 = <Feature>{t24}</Feature>;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("PricingCard.EventBenefits.downloadable");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <Feature>{t26}</Feature>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t("PricingCard.EventBenefits.upload");
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t28) {
    t29 = <Feature bold={true}>{t28}</Feature>;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t) {
    t30 = t("PricingCard.EventBenefits.storage");
    $[64] = t;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t30) {
    t31 = <Feature bold={true}>{t30}</Feature>;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== tPricing) {
    t32 = tPricing("chooseDateNote");
    $[68] = tPricing;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t32) {
    t33 = <FeatureDate>{t32}</FeatureDate>;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t23 || $[73] !== t25 || $[74] !== t27 || $[75] !== t29 || $[76] !== t31 || $[77] !== t33) {
    t34 = <div className="flex flex-col gap-2">{t23}{t25}{t27}{t29}{t31}{t33}</div>;
    $[72] = t23;
    $[73] = t25;
    $[74] = t27;
    $[75] = t29;
    $[76] = t31;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== thankYouCardPromotion) {
    t35 = <ThankYouCardsPromotion promotion={thankYouCardPromotion} />;
    $[79] = thankYouCardPromotion;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  const t36 = thankYouCardPromotion?.offerPlacement ?? offerPlacement;
  let t37;
  if ($[81] !== offer || $[82] !== t36) {
    t37 = <BuyEventButton variant="white" packageType="premium" offerPlacement={t36} offer={offer} />;
    $[81] = offer;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t19 || $[85] !== t21 || $[86] !== t34 || $[87] !== t35 || $[88] !== t37) {
    t38 = <CardContent className="flex flex-col gap-4">{t19}{t21}{t34}{t35}{t37}</CardContent>;
    $[84] = t19;
    $[85] = t21;
    $[86] = t34;
    $[87] = t35;
    $[88] = t37;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t18 || $[91] !== t38 || $[92] !== t7) {
    t39 = <Card className="relative flex flex-col justify-between border-none text-white" style={t2}>{t7}{t18}{t38}</Card>;
    $[90] = t18;
    $[91] = t38;
    $[92] = t7;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  return t39;
}
export function PricingCardsBarMitsva(t0) {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offerPlacement,
    offers,
    thankYouCardPromotion
  } = t1;
  const t = useTranslations("Pricing");
  const t2 = offers?.basic;
  let t3;
  if ($[2] !== offerPlacement || $[3] !== t2) {
    t3 = <PricingCardBasicBarMitsva offer={t2} offerPlacement={offerPlacement} />;
    $[2] = offerPlacement;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = offers?.premium;
  let t5;
  if ($[5] !== offerPlacement || $[6] !== t4 || $[7] !== thankYouCardPromotion) {
    t5 = <PricingCardPremiumBarMitsva offer={t4} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} />;
    $[5] = offerPlacement;
    $[6] = t4;
    $[7] = thankYouCardPromotion;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      color: "rgba(26, 42, 80, 0.5)"
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("fairUsePolicy");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Link href="/terms-and-conditions" className="underline" target="_blank">fair use policy</Link>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8) {
    t10 = <p className="text-xs" style={t7}>*{t8}{" "}{t9}.</p>;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <div className="flex flex-col gap-2 items-end">{t6}{t10}</div>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export function PricingCardsChristmas(t0) {
  const $ = _c(20);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offerPlacement,
    offers,
    thankYouCardPromotion
  } = t1;
  const t = useTranslations("Pricing");
  const t2 = offers?.basic;
  let t3;
  if ($[2] !== offerPlacement || $[3] !== t2) {
    t3 = <PricingCardLightChristmas offer={t2} offerPlacement={offerPlacement} />;
    $[2] = offerPlacement;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = offers?.premium;
  let t5;
  if ($[5] !== offerPlacement || $[6] !== t4 || $[7] !== thankYouCardPromotion) {
    t5 = <PricingCardDarkChristmas offer={t4} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} />;
    $[5] = offerPlacement;
    $[6] = t4;
    $[7] = thankYouCardPromotion;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("fairUsePolicy");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Link href="/terms-and-conditions" className="underline" target="_blank">fair use policy</Link>;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7) {
    t9 = <p className="text-xs text-dark/50">*{t7}{" "}{t8}.</p>;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <div className="flex flex-col gap-2 items-end">{t6}{t9}</div>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}